import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useStore } from '../context/ProductStore';
import { Save, ArrowLeft, Image as ImageIcon, Type, Calendar, Clock, Tag } from 'lucide-react';
import { uploadImage } from '../utils/upload';
import '../components/ProductForm.css';
import './BlogsAdmin.css';
import { Bold, Italic, List, Link as LinkIcon, Type as TypeIcon } from 'lucide-react';

export default function BlogForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { blogs, addBlog, updateBlog } = useStore();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    image: '',
    category: 'Health Tips',
    date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    readTime: '5 min read',
    status: 'published'
  });

  useEffect(() => {
    if (isEdit && blogs.length > 0) {
      const blog = blogs.find(b => (b._id === id || b.id === id));
      if (blog) {
        setFormData({
          title: blog.title || '',
          slug: blog.slug || '',
          excerpt: blog.excerpt || '',
          content: blog.content || '',
          image: blog.image || '',
          category: blog.category || 'Health Tips',
          date: blog.date || '',
          readTime: blog.readTime || '5 min read',
          status: blog.status || 'published'
        });
      }
    }
  }, [isEdit, id, blogs]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      const updated = { ...prev, [name]: value };
      if (name === 'title' && !isEdit) {
        updated.slug = value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
      }
      return updated;
    });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setLoading(true);
      try {
        const url = await uploadImage(file);
        setFormData(prev => ({ ...prev, image: url }));
      } catch (err) {
        alert('Upload failed');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.slug) {
      alert('Title and Slug are required');
      return;
    }
    setLoading(true);
    try {
      if (isEdit) {
        await updateBlog(id, formData);
      } else {
        await addBlog(formData);
      }
      alert('Blog post saved successfully!');
      navigate('/admin/blogs');
    } catch (err) {
      alert(err.message || 'Failed to save blog');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="product-form-page blog-form-container">
      <div className="admin-page-header">
        <div className="header-with-back">
          <button className="back-btn" onClick={() => navigate('/admin/blogs')}>
            <ArrowLeft size={20} />
          </button>
          <div>
            <h1 className="admin-page-title" style={{ fontFamily: 'var(--font-display)' }}>
              {isEdit ? 'Edit Blog Post' : 'Add New Blog'}
            </h1>
            <p className="admin-page-sub">{isEdit ? `Editing: ${formData.title}` : 'Create a new article for your journal'}</p>
          </div>
        </div>
        <button 
          className={`admin-btn-primary ${loading ? 'loading' : ''}`} 
          onClick={handleSubmit}
          disabled={loading}
          style={{ padding: '12px 24px', borderRadius: '12px' }}
        >
          <Save size={18} /> {loading ? 'Saving...' : 'Save Blog Post'}
        </button>
      </div>

      <form className="admin-form-grid" onSubmit={handleSubmit}>
        <div className="admin-form-main">
          <div className="admin-card" style={{ borderRadius: '20px', padding: '32px' }}>
            <h3 className="card-title" style={{ marginBottom: '24px' }}><TypeIcon size={20} /> Article Content</h3>
            
            <div className="admin-field">
              <label style={{ fontWeight: 600 }}>Title</label>
              <input 
                type="text" 
                name="title"
                className="admin-input-premium"
                value={formData.title}
                onChange={handleChange}
                placeholder="e.g. 7 Powerful Benefits of Eating Almonds Daily"
                required
                style={{ fontSize: '18px', fontWeight: 600 }}
              />
            </div>

            <div className="admin-field">
              <label style={{ fontWeight: 600 }}>Slug (URL friendly)</label>
              <div className="slug-input-wrap" style={{ position: 'relative' }}>
                <input 
                  type="text" 
                  name="slug"
                  value={formData.slug}
                  onChange={handleChange}
                  placeholder="e.g. almonds-benefits"
                  required
                />
                <span className="slug-hint" style={{ fontSize: '12px', color: '#94a3b8', marginTop: '4px', display: 'block' }}>
                  Live URL: koadryfruits.com/blog/{formData.slug || '...'}
                </span>
              </div>
            </div>

            <div className="admin-field">
              <label style={{ fontWeight: 600 }}>Excerpt (Short Summary)</label>
              <textarea 
                name="excerpt"
                className="admin-textarea-premium"
                value={formData.excerpt}
                onChange={handleChange}
                rows={3}
                placeholder="A brief summary that appears on the blog list page..."
                required
              />
            </div>

            <div className="admin-field">
              <label style={{ fontWeight: 600 }}>Main Content</label>
              <div className="content-editor-wrap">
                <div className="editor-toolbar-mock">
                  <button type="button" className="toolbar-btn"><Bold size={16} /></button>
                  <button type="button" className="toolbar-btn"><Italic size={16} /></button>
                  <button type="button" className="toolbar-btn"><List size={16} /></button>
                  <button type="button" className="toolbar-btn"><LinkIcon size={16} /></button>
                </div>
                <textarea 
                  name="content"
                  className="admin-textarea-premium"
                  value={formData.content}
                  onChange={handleChange}
                  rows={15}
                  placeholder="Write your article here. You can use HTML tags for formatting..."
                  style={{ border: 'none', borderRadius: 0 }}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="admin-form-sidebar">
          <div className="admin-card" style={{ borderRadius: '20px' }}>
            <h3 className="card-title"><ImageIcon size={18} /> Cover Image</h3>
            <div className="image-upload-zone-blog" onClick={() => document.getElementById('blog-img-input').click()}>
              {formData.image ? (
                <img src={formData.image} alt="Preview" className="blog-prev-img" />
              ) : (
                <>
                  <ImageIcon size={40} style={{ color: '#cbd5e1', marginBottom: '12px' }} />
                  <span style={{ fontSize: '14px', color: '#64748b' }}>Click to upload cover</span>
                </>
              )}
              <input 
                id="blog-img-input"
                type="file" 
                accept="image/*" 
                onChange={handleFileUpload} 
                style={{ display: 'none' }} 
              />
            </div>
            <div className="admin-field" style={{ marginTop: '16px' }}>
              <label>Image URL</label>
              <input 
                type="text" 
                name="image"
                value={formData.image}
                onChange={handleChange}
                placeholder="https://..."
              />
            </div>
          </div>

          <div className="admin-card" style={{ borderRadius: '20px' }}>
            <h3 className="card-title"><Tag size={18} /> Settings</h3>
            
            <div className="admin-field">
              <label>Category</label>
              <select name="category" value={formData.category} onChange={handleChange} className="admin-select-premium">
                <option value="Health Tips">Health Tips</option>
                <option value="Buying Guide">Buying Guide</option>
                <option value="Superfoods">Superfoods</option>
                <option value="Recipes">Recipes</option>
              </select>
            </div>

            <div className="admin-field">
              <label>Reading Time</label>
              <div className="hp-input-group">
                <Clock size={16} style={{ color: '#94a3b8' }} />
                <input 
                  type="text" 
                  name="readTime"
                  value={formData.readTime}
                  onChange={handleChange}
                  placeholder="e.g. 5 min read"
                />
              </div>
            </div>

            <div className="admin-field">
              <label>Status</label>
              <div className="status-toggle-premium">
                <button 
                  type="button"
                  className={`status-option published ${formData.status === 'published' ? 'active' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, status: 'published' }))}
                >
                  Published
                </button>
                <button 
                  type="button"
                  className={`status-option draft ${formData.status === 'draft' ? 'active' : ''}`}
                  onClick={() => setFormData(prev => ({ ...prev, status: 'draft' }))}
                >
                  Draft
                </button>
              </div>
            </div>
          </div>

          <div className="admin-card form-footer-card" style={{ borderRadius: '20px', padding: '24px', textAlign: 'center', marginTop: '24px' }}>
            <button 
              type="button" 
              className={`admin-btn-primary ${loading ? 'loading' : ''}`} 
              onClick={handleSubmit}
              disabled={loading}
              style={{ width: '100%', padding: '16px', borderRadius: '12px', fontSize: '16px' }}
            >
              <Save size={20} /> {loading ? 'Saving...' : (isEdit ? 'Update Blog Post' : 'Publish Blog Post')}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
